import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import type {
  BodyMeasurementsItem,
  BodyPartKey,
  ClientConfiguration as ClientConfigurationType,
  ReadinessItem,
  TrackerKey,
  WeekdayIndex,
} from "../../../../../types/clientConfig";
import { ConfigSwitch } from "./ConfigSwitch";
import { DaySchedulePicker } from "./DaySchedulePicker";
import type { ClientConfigRoleLabels } from "./i18n";
import {
  BODY_PART_I18N_KEYS,
  BODY_PART_KEYS,
  FULL_WEEK,
  isSyncableTracker,
  rotateWeekdayDisplayOrder,
  TRACKER_ROWS,
} from "./utils";

const TrackerTitleRow = ({
  title,
  trackerKey,
  syncableLabel,
}: {
  title: string;
  trackerKey: TrackerKey;
  syncableLabel: string;
}) => (
  <div className="client-config-tracker-row__title">
    <p>{title}</p>
    {isSyncableTracker(trackerKey) && (
      <span className="chip info client-config-syncable-chip">
        {syncableLabel}
      </span>
    )}
  </div>
);

type ForcedPanelProps = {
  forced: boolean;
  roleLabels: ClientConfigRoleLabels;
  onChange?: (newStatus: boolean) => void;
};

/** Coach-owned "Forced" option nested under the Training Readiness row; read-only when no `onChange`. */
const ForcedPanel = ({ forced, roleLabels, onChange }: ForcedPanelProps) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <div
      className={`client-config-forced-panel${
        forced ? " client-config-forced-panel--on" : ""
      }`}
    >
      <SvgIcon
        src="/img/new-icons/lock.svg"
        useCurrentColor
        classNames="client-config-forced-panel__icon"
      />
      <div className="client-config-forced-panel__text">
        <p className="client-config-forced-panel__title">
          {t("clientConfiguration.trackers.readiness.forced.title")}
        </p>
        <p className="client-config-forced-panel__desc">
          {t("clientConfiguration.trackers.readiness.forced.desc")}
        </p>
      </div>
      {onChange && (
        <ConfigSwitch
          id="client-config-readiness-forced"
          ariaLabel={t("clientConfiguration.aria.forcedReadiness", roleLabels)}
          checked={forced}
          onChange={onChange}
        />
      )}
    </div>
  );
};

type ReadOnlyProps = {
  readOnly: true;
  roleLabels: ClientConfigRoleLabels;
  selectedTemplate: ClientConfigurationType;
  /** 0 = Sun … 6 = Sat; only reorders the schedule summary text. Defaults to Sunday-first. */
  startingCalendarDay?: number;
};

type EditableProps = {
  readOnly?: false;
  roleLabels: ClientConfigRoleLabels;
  currentConfig: ClientConfigurationType;
  onBodyPartsChanged: (parts: BodyPartKey[]) => void;
  onScheduleChanged: (key: TrackerKey, schedule: WeekdayIndex[]) => void;
  onTrackerClick: (key: TrackerKey, newStatus: boolean) => void;
  /** When omitted the Forced panel is not rendered. */
  onForcedChange?: (newStatus: boolean) => void;
  /** 0 = Sun … 6 = Sat; only reorders the weekday chips. Defaults to Sunday-first. */
  startingCalendarDay?: number;
};

type TrackingItemsProps = ReadOnlyProps | EditableProps;

export const TrackingItems = (props: TrackingItemsProps) => {
  const { t } = useTranslation("athlete-portal");
  const weekdayLong = t("clientConfiguration.weekdays.long", {
    returnObjects: true,
  }) as string[];

  if (props.readOnly === true) {
    const { selectedTemplate, roleLabels, startingCalendarDay = 0 } = props;
    const displayDayOrder = rotateWeekdayDisplayOrder(startingCalendarDay);
    return (
      <div className="client-config-tracker-list">
        {TRACKER_ROWS.map((row) => {
          const item = selectedTemplate?.sections?.tracking?.items?.[row.key];
          const coachRequired = !!item?.coachRequired;
          const isEnabled = !!item?.coachRequired || !!item?.clientEnabled;
          const clientOverride = !!item?.clientEnabled;
          const showClientOverrideChrome = clientOverride && !coachRequired;
          const schedule = item && "schedule" in item ? item.schedule : null;
          const isEveryday =
            schedule?.length === 7 &&
            FULL_WEEK.every((d) => schedule?.includes(d));
          const activeBodyParts: string[] =
            row.key === "bodyMeasurements"
              ? (item as { bodyParts?: string[] })?.bodyParts ?? [
                  ...BODY_PART_KEYS,
                ]
              : [];
          if (!isEnabled) return null;
          const trackerTitle = t(row.titleKey);
          return (
            <div
              key={row.key}
              className={`client-config-tracker-row${
                showClientOverrideChrome ? " client-override" : ""
              }`}
            >
              <div className="left-content">
                <div className="not-clickable-content">
                  <div className="icon-wrapper">
                    <SvgIcon src={`/img/new-icons/${row.icon}`} />
                  </div>
                  <div className="client-config-tracker-row__text">
                    <TrackerTitleRow
                      title={trackerTitle}
                      trackerKey={row.key}
                      syncableLabel={t("clientConfiguration.chips.syncable")}
                    />
                    <p>{t(row.descKey)}</p>
                  </div>
                </div>
                {clientOverride && !coachRequired && (
                  <span className="chip warning">
                    {t("clientConfiguration.chips.clientOverride", roleLabels)}
                  </span>
                )}
                {coachRequired && !schedule && (
                  <span>
                    {t("clientConfiguration.schedule.trainingDaysOnlyParens")}
                  </span>
                )}
                {coachRequired && schedule && (
                  <span>
                    (
                    {isEveryday
                      ? t("clientConfiguration.schedule.everyday")
                      : displayDayOrder
                          .filter((day) => schedule?.includes(day))
                          .map((day: WeekdayIndex) => weekdayLong[day])
                          .join(", ")}
                    )
                  </span>
                )}
              </div>
              {row.key === "readiness" &&
                coachRequired &&
                (item as ReadinessItem)?.forced && (
                  <ForcedPanel forced roleLabels={roleLabels} />
                )}
              {coachRequired && row.key === "bodyMeasurements" && (
                <div className="client-config-body-parts-wrapper">
                  {activeBodyParts?.map((bodyPart: string) => (
                    <span key={bodyPart} className="chip success">
                      {t(
                        `clientConfiguration.bodyParts.${BODY_PART_I18N_KEYS[bodyPart as BodyPartKey]}`
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  const {
    currentConfig,
    onBodyPartsChanged,
    onScheduleChanged,
    onTrackerClick,
    onForcedChange,
    roleLabels,
    startingCalendarDay = 0,
  } = props;

  return (
    <div className="client-config-tracker-list">
      {TRACKER_ROWS.map((row) => {
        const item = currentConfig?.sections?.tracking?.items?.[row.key];
        const coachRequired = !!item?.coachRequired;
        const isEnabled = !!item?.coachRequired || !!item?.clientEnabled;
        const clientOverride = !!item?.clientEnabled;
        const showClientOverrideChrome = clientOverride && !coachRequired;
        const schedule = item && "schedule" in item ? item.schedule : null;
        const bodyMeasurementsItem =
          row.key === "bodyMeasurements"
            ? (item as BodyMeasurementsItem)
            : undefined;
        // Client elected their own subset from the app — until the coach touches a body
        // part again (which clears it), that selection is the source of truth to show here.
        const clientBodyPartsOverride = bodyMeasurementsItem?.clientBodyParts;
        const hasClientBodyPartsOverride = clientBodyPartsOverride != null;
        const activeBodyParts: string[] = bodyMeasurementsItem
          ? clientBodyPartsOverride ??
            bodyMeasurementsItem.bodyParts ?? [...BODY_PART_KEYS]
          : [];
        const trackerTitle = t(row.titleKey);
        return (
          <div
            key={row.key}
            className={`client-config-tracker-row${
              showClientOverrideChrome ? " client-override" : ""
            }${!isEnabled ? " client-config-tracker-row--disabled" : ""}`}
          >
            <div className="left-content">
              <button
                type="button"
                className="clickable-content"
                onClick={() => {
                  onTrackerClick(row.key, !isEnabled);
                }}
              >
                <ConfigSwitch
                  id={`client-config-tracker-${row.key}`}
                  ariaLabel={t("clientConfiguration.aria.requireTracker", {
                    tracker: trackerTitle,
                  })}
                  checked={isEnabled}
                  onChange={(newStatus: boolean) =>
                    onTrackerClick(row.key, newStatus)
                  }
                />
                <div className="icon-wrapper">
                  <SvgIcon src={`/img/new-icons/${row.icon}`} />
                </div>
                <div className="client-config-tracker-row__text">
                  <TrackerTitleRow
                    title={trackerTitle}
                    trackerKey={row.key}
                    syncableLabel={t("clientConfiguration.chips.syncable")}
                  />
                  <p>{t(row.descKey)}</p>
                </div>
              </button>
              {clientOverride && !coachRequired && (
                <span className="chip warning">
                  {t("clientConfiguration.chips.clientOverride", roleLabels)}
                </span>
              )}
              {coachRequired && !schedule && (
                <span className="chip info">
                  {t("clientConfiguration.schedule.trainingDaysOnly")}
                </span>
              )}
              {coachRequired && schedule && (
                <DaySchedulePicker
                  schedule={schedule}
                  disabled={!coachRequired}
                  startingCalendarDay={startingCalendarDay}
                  onChange={(newSchedule: WeekdayIndex[]) =>
                    onScheduleChanged(row.key, newSchedule)
                  }
                />
              )}
            </div>
            {row.key === "readiness" && coachRequired && onForcedChange && (
              <ForcedPanel
                forced={!!(item as ReadinessItem)?.forced}
                roleLabels={roleLabels}
                onChange={onForcedChange}
              />
            )}
            {coachRequired && row.key === "bodyMeasurements" && (
              <div className="client-config-body-parts-wrapper">
                <span>{t("clientConfiguration.schedule.bodyParts")}</span>
                {hasClientBodyPartsOverride && (
                  <span className="chip warning">
                    {t("clientConfiguration.chips.clientOverride", roleLabels)}
                  </span>
                )}
                <div className="body-parts">
                  {BODY_PART_KEYS.map((part) => {
                    const checked = activeBodyParts.includes(part);
                    return (
                      <button
                        key={part}
                        type="button"
                        className={`body-part${checked ? " active" : ""}`}
                        onClick={() => {
                          const next = checked
                            ? activeBodyParts.filter((p) => p !== part)
                            : [...activeBodyParts, part];
                          onBodyPartsChanged(next as BodyPartKey[]);
                        }}
                      >
                        {t(
                          `clientConfiguration.bodyParts.${BODY_PART_I18N_KEYS[part]}`
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
